/**
 * POs Late to Raise (CR-8623) — the forecast rows whose order-by date has
 * passed, most overdue first. Each row opens the part's order history, so a
 * buyer can see who supplied it before and at what price without looking the
 * part up by hand.
 *
 * Signed off as mockup 02 (first pass): no Supplier column; parts never
 * ordered before are hidden by default.
 */
import { useCallback, useEffect, useState } from 'react';
import { lateToRaiseAPI, type LateBand, type LateToRaiseRow } from '../../../../lib/api';
import { useView } from '../../../../contexts/ViewContext';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Checkbox } from '../../../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { DrilldownPager } from '../../../dashboard/po-lines/DrilldownPager';
import { ChevronRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { notifyExportResult } from '../../../../lib/exportTruncation';
import OrderHistoryModal from './OrderHistoryModal';
import { LateBadge } from './LateBadge';

const PAGE_SIZE = 25;

const BANDS: { value: LateBand; label: string }[] = [
  { value: '', label: 'All' },
  { value: '0-7', label: 'Up to 7 days' },
  { value: '8-30', label: '8 to 30 days' },
  { value: '31-', label: 'Over 30 days' },
];

export default function LateToRaiseTab() {
  const { formatDate, formatCurrency, formatNumber } = useFormatters();
  // The top-bar site/company/group, sent and followed as the Forecast tab does.
  const { viewKey, getViewQueryParams } = useView();
  const [rows, setRows] = useState<LateToRaiseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [band, setBand] = useState<LateBand>('');
  const [hideNeverOrdered, setHideNeverOrdered] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [openRow, setOpenRow] = useState<LateToRaiseRow | null>(null);

  // Debounce the search box so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => { setAppliedSearch(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // viewKey is a dependency so a site switch re-reads; getViewQueryParams reads the live view.
  const params = useCallback(() => ({
    ...getViewQueryParams(),
    search: appliedSearch || undefined,
    late: band || undefined,
    hide_never_ordered: hideNeverOrdered,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [appliedSearch, band, hideNeverOrdered, viewKey]);

  // A site switch starts again from page 1.
  useEffect(() => { setPage(1); }, [viewKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    lateToRaiseAPI.list({ ...params(), page, page_size: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data.results);
        setTotal(res.data.count);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Late to raise load failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load POs late to raise');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params, page]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await lateToRaiseAPI.export(params());
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pos-late-to-raise-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      notifyExportResult(resp, 'Excel export ready');
    } catch (err: unknown) {
      console.error('Late to raise export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const historyLabel = (row: LateToRaiseRow) => {
    if (!row.order_count) {
      return <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">No previous orders</span>;
    }
    const orders = `${row.order_count} order${row.order_count === 1 ? '' : 's'}`;
    const suppliers = `${row.supplier_count} supplier${row.supplier_count === 1 ? '' : 's'}`;
    return <span className="font-medium text-primary">{orders}, {suppliers}</span>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>POs Late to Raise</CardTitle>
          <CardDescription>
            Forecast orders whose order-by date has passed, most overdue first. Click a row for its order history.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export to Excel
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input
            className="w-full sm:w-72"
            type="search"
            placeholder="Search part number or description"
            aria-label="Search part number or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-wrap gap-1" role="group" aria-label="Days late">
            {BANDS.map((b) => (
              <Button
                key={b.label}
                type="button"
                size="sm"
                variant={band === b.value ? 'default' : 'outline'}
                className="rounded-full"
                aria-pressed={band === b.value}
                onClick={() => { setBand(b.value); setPage(1); }}
              >
                {b.label}
              </Button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={hideNeverOrdered}
              onCheckedChange={(checked) => { setHideNeverOrdered(checked === true); setPage(1); }}
            />
            Hide parts never ordered before
          </label>
        </div>

        {error && <div className="py-3 text-sm text-red-600">{error}</div>}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part Number</TableHead>
                <TableHead>Part Description</TableHead>
                <TableHead className="text-right">Qty to Order</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead>Order By</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>Order History</TableHead>
                <TableHead className="text-right">Last Price</TableHead>
                <TableHead aria-hidden="true" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                    Nothing is late to raise for these filters.
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow
                  key={row.id}
                  tabIndex={0}
                  className="cursor-pointer"
                  onClick={() => setOpenRow(row)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenRow(row); } }}
                >
                  <TableCell className="font-mono text-sm">{row.part_number}</TableCell>
                  <TableCell>{row.description || <span className="text-muted-foreground">Not known</span>}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(Number(row.qty_to_order))}</TableCell>
                  <TableCell>{row.unit_of_measure || <span className="text-muted-foreground">-</span>}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(row.order_by)}</TableCell>
                  <TableCell><LateBadge days={row.days_late} /></TableCell>
                  <TableCell className="whitespace-nowrap">{historyLabel(row)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.last_price !== null
                      ? formatCurrency(Number(row.last_price), row.last_price_currency || undefined, 2)
                      : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground"><ChevronRight className="h-4 w-4" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {total > PAGE_SIZE && (
          <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} idPrefix="ltr" />
        )}
      </CardContent>

      <OrderHistoryModal row={openRow} onClose={() => setOpenRow(null)} />
    </Card>
  );
}
